<template>
  <header class="top">
    <div class="left">
      <EcosaWordmark :size="20" />
      <span class="divider" />
      <span class="product">AI Product Manager</span>
    </div>
    <div class="right">
      <button class="icon-btn" title="Help">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10" />
          <path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" />
          <path d="M12 17h.01" />
        </svg>
      </button>
      <button class="icon-btn" title="Notifications">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
      </button>

      <div class="user-wrap" ref="userWrapRef">
        <button class="user" :class="{ open: menuOpen }" @click="menuOpen = !menuOpen">
          <Avatar kind="user" :initials="initials" :size="26" />
          <div class="user-text">
            <span class="user-name">{{ displayName }}</span>
            <span class="user-email">{{ auth.me?.email ?? "" }}</span>
          </div>
          <svg
            class="user-caret"
            width="12" height="12" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>

        <div v-if="menuOpen" class="menu">
          <div class="menu-header">
            <Avatar kind="user" :initials="initials" :size="36" />
            <div class="menu-id">
              <span class="menu-name">{{ displayName }}</span>
              <span class="menu-email">{{ auth.me?.email ?? "" }}</span>
            </div>
          </div>
          <div class="menu-sep" />
          <button class="menu-item danger" @click="onLogout">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Sign out
          </button>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed, ref, watch, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import EcosaWordmark from "@/components/design/EcosaWordmark.vue";
import Avatar from "@/components/design/Avatar.vue";
import { displayNameFor, initialsFor } from "@/lib/user";

const auth = useAuthStore();
const router = useRouter();

const menuOpen = ref(false);
const userWrapRef = ref<HTMLElement | null>(null);

const displayName = computed(() => displayNameFor(auth.me));
const initials = computed(() => initialsFor(displayName.value));

function onLogout() {
  menuOpen.value = false;
  auth.logout();
  router.push({ name: "login" });
}

function onDocumentClick(e: MouseEvent) {
  const wrap = userWrapRef.value;
  if (wrap && !wrap.contains(e.target as Node)) {
    menuOpen.value = false;
  }
}
function onEsc(e: KeyboardEvent) {
  if (e.key === "Escape") menuOpen.value = false;
}

watch(menuOpen, (open) => {
  if (open) {
    document.addEventListener("mousedown", onDocumentClick);
    document.addEventListener("keydown", onEsc);
  } else {
    document.removeEventListener("mousedown", onDocumentClick);
    document.removeEventListener("keydown", onEsc);
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("mousedown", onDocumentClick);
  document.removeEventListener("keydown", onEsc);
});
</script>

<style scoped>
.top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 18px;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
}
.left {
  display: flex;
  align-items: center;
  gap: 16px;
}
.divider {
  width: 1px;
  height: 18px;
  background: var(--border-2);
}
.product {
  font-size: 13px;
  font-weight: 500;
  color: var(--ink-2);
  letter-spacing: -0.005em;
}
.right {
  display: flex;
  align-items: center;
  gap: 14px;
}
.icon-btn {
  width: 30px;
  height: 30px;
  background: transparent;
  border: none;
  color: var(--ink-3);
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.icon-btn:hover {
  background: var(--hover);
  color: var(--ink);
}

.user-wrap {
  position: relative;
}
.user {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px 4px 4px;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.15s;
  background: transparent;
  border: 1px solid transparent;
  font-family: inherit;
}
.user:hover,
.user.open {
  background: var(--hover);
}
.user.open {
  border-color: var(--border-2);
  background: var(--surface);
}
.user-text {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
  text-align: left;
}
.user-name {
  font-size: 12.5px;
  font-weight: 500;
  color: var(--ink);
}
.user-email {
  font-size: 11px;
  color: var(--ink-4);
}
.user-caret {
  color: var(--ink-4);
  margin-left: 2px;
  transition: transform 0.15s;
}
.user.open .user-caret {
  transform: rotate(180deg);
}

.menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 240px;
  background: var(--surface);
  border: 1px solid var(--border-2);
  border-radius: 12px;
  padding: 8px;
  box-shadow: 0 20px 48px rgba(11, 15, 20, 0.12), 0 2px 6px rgba(11, 15, 20, 0.06);
  z-index: 50;
}
.menu-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px 10px;
}
.menu-id {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.menu-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.menu-email {
  font-size: 11.5px;
  color: var(--ink-4);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.menu-sep {
  height: 1px;
  background: var(--border);
  margin: 4px 0;
}
.menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  background: transparent;
  border: none;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 13px;
  color: var(--ink-2);
  cursor: pointer;
  text-align: left;
  transition: background 0.12s, color 0.12s;
  font-family: inherit;
}
.menu-item:hover {
  background: var(--hover);
  color: var(--ink);
}
.menu-item.danger {
  color: #b03333;
}
.menu-item.danger:hover {
  background: #fdecec;
  color: #8a2a2a;
}
</style>
